import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from './Layout';
import ListPager from '../components/ListPager';

const API = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
const LIMIT = 20;

const FIELD_LABELS = {
  last_name: 'Фамилия',
  first_name: 'Имя',
  middle_name: 'Отчество',
  birth_date: 'Дата рождения',
  phone: 'Телефон',
  specialty: 'Специальность',
  tax_status: 'Налоговый статус',
  inn: 'ИНН',
  bank_name: 'Банк',
  bank_bik: 'БИК банка',
  bank_account: 'Расчётный счёт',
  bank_corr_account: 'Корр. счёт',
  ogrnip: 'ОГРНИП',
};
const TAX_LABEL = { self_employed: 'Самозанятый', individual_entrepreneur: 'ИП' };

function fmtVal(field, v) {
  if (v === null || v === undefined || v === '') return '—';
  if (field === 'tax_status') return TAX_LABEL[v] || v;
  if (field === 'birth_date') return new Date(v).toLocaleDateString('ru');
  return v;
}

export default function ProfileRequests() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('pending');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [busyId, setBusyId] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [msg, setMsg] = useState('');

  const key = localStorage.getItem('adminKey');
  const headers = { 'X-Admin-Key': key };

  const fetchItems = useCallback(() => {
    axios.get(`${API}/admin/profile-requests`, { headers, params: { status, limit: LIMIT, offset } })
      .then(r => { setItems(r.data.items); setTotal(r.data.total); });
  }, [status, offset]); // eslint-disable-line
  useEffect(() => { fetchItems(); }, [fetchItems]);

  const approve = async (id) => {
    setBusyId(id); setMsg('');
    try { await axios.post(`${API}/admin/profile-requests/${id}/approve`, {}, { headers }); fetchItems(); }
    catch (e) { setMsg(e?.response?.data?.detail || 'Не удалось принять заявку'); }
    finally { setBusyId(null); }
  };

  const reject = async () => {
    if (!rejectReason.trim()) { setMsg('Укажите причину отказа'); return; }
    setBusyId(rejectFor); setMsg('');
    try {
      await axios.post(`${API}/admin/profile-requests/${rejectFor}/reject`, {}, { headers, params: { reason: rejectReason } });
      setRejectFor(null); setRejectReason(''); fetchItems();
    } catch (e) { setMsg(e?.response?.data?.detail || 'Не удалось отклонить заявку'); }
    finally { setBusyId(null); }
  };

  return (
    <Layout>
      <div style={styles.header}>
        <h1 style={styles.title}>Заявки на изменение профиля</h1>
      </div>

      <div style={styles.filterBar}>
        {[['pending', 'На рассмотрении'], ['approved', 'Принятые'], ['rejected', 'Отклонённые']].map(([v, l]) => (
          <button key={v} style={{ ...styles.modeBtn, ...(status === v ? styles.modeBtnActive : {}) }}
            onClick={() => { setStatus(v); setOffset(0); }}>{l}</button>
        ))}
      </div>

      {msg && <div style={styles.errText}>{msg}</div>}

      <div style={styles.list}>
        {items.map(r => (
          <div key={r.id} style={styles.card}>
            <div style={styles.cardHeader}>
              <span style={styles.link} onClick={() => navigate(`/doctors/${r.doctor_id}`)}>{r.doctor_name}</span>
              <span style={styles.date}>{new Date(r.requested_at).toLocaleString('ru')}</span>
            </div>
            <div style={styles.diffGrid}>
              {Object.entries(r.changes).map(([field, diff]) => (
                <div key={field} style={styles.diffRow}>
                  <span style={styles.diffField}>{FIELD_LABELS[field] || field}</span>
                  <span style={styles.diffOld}>{fmtVal(field, diff.old)}</span>
                  <span style={styles.diffArrow}>→</span>
                  <span style={styles.diffNew}>{fmtVal(field, diff.new)}</span>
                </div>
              ))}
            </div>
            {r.status === 'pending' && (
              <div style={styles.actions}>
                <button style={styles.btn} onClick={() => approve(r.id)} disabled={busyId === r.id}>Принять</button>
                <button style={styles.btnDanger} onClick={() => { setMsg(''); setRejectReason(''); setRejectFor(r.id); }} disabled={busyId === r.id}>Отклонить</button>
              </div>
            )}
            {r.status === 'rejected' && r.reject_reason && (
              <div style={styles.warnNote}>Причина отказа: {r.reject_reason}</div>
            )}
          </div>
        ))}
        {items.length === 0 && <div style={styles.empty}>Заявок нет</div>}
      </div>
      <ListPager total={total} limit={LIMIT} offset={offset} onPage={setOffset} />

      {rejectFor && (
        <div style={styles.modalOverlay} onClick={() => setRejectFor(null)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Отклонить заявку?</h3>
            <p style={styles.modalText}>Врач увидит причину в кабинете и сможет отправить заявку заново.</p>
            <div style={styles.fieldLabel}>Причина отказа</div>
            <textarea style={styles.modalTextarea} rows={3} placeholder="Например: реквизиты не соответствуют налоговому статусу"
              value={rejectReason} onChange={e => setRejectReason(e.target.value)} autoFocus />
            {msg && <div style={styles.errText}>{msg}</div>}
            <div style={styles.modalActions}>
              <button style={styles.btnGhost} onClick={() => setRejectFor(null)}>Отмена</button>
              <button style={styles.btnDanger} onClick={reject} disabled={busyId === rejectFor}>Отклонить</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

const styles = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  title: { color: 'white', fontSize: 24, fontWeight: 700, margin: 0 },
  filterBar: { display: 'flex', gap: 8, marginBottom: 20 },
  modeBtn: { background: '#1e293b', color: '#94a3b8', border: '1px solid #334155', padding: '7px 16px', borderRadius: 8, fontSize: 13, cursor: 'pointer', fontWeight: 500 },
  modeBtnActive: { background: '#2EC4B6', color: 'white', border: '1px solid #2EC4B6' },
  list: { display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 },
  card: { background: '#1e293b', borderRadius: 12, padding: 18 },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  link: { color: '#2EC4B6', cursor: 'pointer', fontWeight: 600, fontSize: 15 },
  date: { color: '#64748b', fontSize: 12 },
  diffGrid: { display: 'flex', flexDirection: 'column', gap: 6 },
  diffRow: { display: 'grid', gridTemplateColumns: '160px 1fr 20px 1fr', gap: 8, alignItems: 'center', fontSize: 13 },
  diffField: { color: '#64748b' },
  diffOld: { color: '#94a3b8', textDecoration: 'line-through' },
  diffArrow: { color: '#475569', textAlign: 'center' },
  diffNew: { color: '#e2e8f0', fontWeight: 600 },
  actions: { display: 'flex', gap: 8, marginTop: 14 },
  btn: { background: '#2EC4B6', color: '#0f172a', border: 'none', borderRadius: 6, padding: '7px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnGhost: { background: 'transparent', color: '#94a3b8', border: '1px solid #334155', borderRadius: 6, padding: '7px 16px', fontSize: 13, cursor: 'pointer' },
  btnDanger: { background: '#450a0a', color: '#f87171', border: '1px solid #7f1d1d', borderRadius: 6, padding: '7px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  warnNote: { marginTop: 10, padding: '8px 12px', background: '#422006', border: '1px solid #854d0e', borderRadius: 8, color: '#fcd34d', fontSize: 12 },
  empty: { color: '#64748b', fontSize: 14, textAlign: 'center', padding: '32px 0' },
  errText: { color: '#f87171', fontSize: 13, marginBottom: 12 },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 },
  modal: { background: '#1e293b', borderRadius: 12, padding: 24, width: 440, maxWidth: '90vw', border: '1px solid #334155' },
  modalTitle: { color: 'white', fontSize: 18, fontWeight: 700, margin: '0 0 12px' },
  modalText: { color: '#94a3b8', fontSize: 13, lineHeight: 1.5, margin: '0 0 16px' },
  fieldLabel: { fontSize: 12, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 },
  modalTextarea: { width: '100%', boxSizing: 'border-box', background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 6, padding: '9px 12px', fontSize: 14, outline: 'none', marginBottom: 8, fontFamily: 'inherit', resize: 'vertical' },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 },
};
